import {getDocConfig, getReadConfig, renderDownloadView, viewIdentifiers} from "@/layouts/resources/utils";
import type {PDFViewFetcherParams, PDFViewRendererParams, ReadResponse, ResourceFileConfig} from "@/layouts/resources/types";
import {asString, notifyCatchedException} from "@/modules/utils";
import {PDFReader} from "@/modules/pdf";

export const directInitReadingViewListeners = () => {
    const view = document.querySelector<HTMLElement>(viewIdentifiers.reading.main);
    if (!view) return;

    const loadType = view.getAttribute('data-load') || 'direct';

    if (loadType === 'on-demand') return;

    const readingConfig = getReadConfig(view, '[name="__rconfig__"]');
    const fileConfig = getDocConfig(view, '[name="__dconfig__"]');

    if (!readingConfig || !fileConfig) {
        notifyCatchedException(new Error(JSON.stringify({fn: 'directInitReadingViewListeners', msg: 'Undefined readingConfig || fileConfig'})));
        return;
    }

    fetchPDFData({readingConfig, fileConfig, inView: false}).then((instance) => {
        instance.setTheme('auto')
    }).catch((e) => notifyCatchedException(e))
}

export const initOnDemandReadingViewListeners = ({readingConfig, fileConfig}: PDFViewFetcherParams) => {
    return fetchPDFData({readingConfig, fileConfig, inView: true})
}

const fetchPDFData = ({readingConfig, fileConfig, inView}: PDFViewFetcherParams): Promise<PDFReader> => {
    return new Promise((resolve, reject) => {
        fetch(asString(readingConfig.route), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                _token: asString(readingConfig.token),
                name: asString(readingConfig.name),
                id: asString(readingConfig.id),
            })
        }).then(response => response.json()).then((response: ReadResponse) => {
            const data = response.data;
            if (data) {
                const instance = renderReadingView({url: data.url, config: fileConfig, inView});
                resolve(instance);
            } else notifyCatchedException(new Error(JSON.stringify(response)));
        }).catch((e) => {
            notifyCatchedException(e);
            console.error(e);
            reject(e);
        });
    });
}

interface HandleCtaParams {
    type: 'download' | 'infos' | null,
    config: ResourceFileConfig,
    instance: PDFReader,
    hideModals: boolean,
    timeout?: number
}

const __handleCta = ({type, config, instance, hideModals, timeout = 50}: HandleCtaParams) => {
    if (hideModals) {
        $('.modal').modal('hide');
        instance.setTheme('light');
    }

    if (type !== null) setTimeout(() => renderDownloadView(config, type), (hideModals ? timeout : 0));
}

const renderReadingView = ({url, config, inView}: PDFViewRendererParams) => {

    if (inView) {
        const engine = document.querySelector(viewIdentifiers.reading.engine);
        engine?.setAttribute('data-inview', 'true');

        const html = document.querySelector('html');
        html.style.setProperty('--re--inview-toolbar-top', '60px')
        html.style.setProperty('--re--inview-sidebar-h', 'calc(100dvh - (var(--re--toolbar-top) * 1.3))')
    }

    const pdfReader = new PDFReader({
        workerSrc: "/storage/vendor/pdf.worker.min.js",
        labels: {
            emptySearch: 'Aucun résultat',
            searchPlaceholder: 'Rechercher ...',
        },
        onDownload: async () => {
            __handleCta({type: 'download', config: config, instance: pdfReader, hideModals: inView, timeout: 50})
        },
        onInfosRequired: async () => {
            __handleCta({type: 'infos', config: config, instance: pdfReader, hideModals: inView, timeout: 50})
        },
        onHomeRequired: async () => {
            __handleCta({type: null, config: config, instance: pdfReader, hideModals: inView, timeout: 50})
        },
        onRenderError: (e) => {
            notifyCatchedException(e, true);
            console.error(e);
        },
    });

    // url = 'https://snippet.embedpdf.com/ebook.pdf'; // testing purpose

    pdfReader.loadFromUrl(url, config.displayName).catch((e) => {
        pdfReader.toggleLoader(false);
        notifyCatchedException(e, true);
        console.error(e)
    })

    return pdfReader;
}

export default directInitReadingViewListeners;